import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trophy, Mountain, Zap, Heart, Radio } from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';

import { useTournamentsQuery } from '@/services/endpoints/tournaments';
import { useListOpenTournamentsQuery, useListMyTournamentsQuery } from '@/services/qduelsApi';
import { useAppSelector } from '@/store/hooks';
import { useGameSelection } from '@/hooks/useGameSelection';
import { useHaptics } from '@/hooks/useHaptics';
import { useHasLogtoRole } from '@/hooks/useHasLogtoRole';
import { TournamentCard } from '@/components/tournaments/TournamentCard';
import { QduelsTournamentCard } from '@/components/duels/QduelsTournamentCard';
import { Loader } from '@/components/common/Loader';
import { cn } from '@/lib/cn';
import { env } from '@/env';
import { listVerdict } from '@/lib/listState';
import { qduelsLegIsPertinent } from '@/lib/tournamentLegs';
import { nextPollInterval, trackPollOutcome, NO_POLL_TRACKING } from '@/lib/pollingBackoff';
import { useStalled, useEverFailed, NO_ANSWER_MS } from '@/hooks/useStalled';
import { CR_GAME_ID } from '@/lib/gameFeatures';
import { SectionCard } from '@/components/hub/SectionCard';
import { EmptyState } from '@/components/common/EmptyState';
import { PanelNotice, PanelWaiting } from '@/components/hub/PanelNotice';
import type { Tournament } from '@/types';
import type { QduelsTournamentSummary } from '@/types/qduels';
import { TOURNAMENT_STATUS } from '@/types/enums';

/**
 * La sezione Limulo, caricata pigra come nel router: entra in un chunk suo, e in una build
 * col flag spento non entra affatto.
 */
const LimuloTournaments = lazy(() =>
  import('./limulo/LimuloTournaments').then((m) => ({ default: m.LimuloTournaments })),
);

const CLOSED_STATUSES = new Set<string>([
  TOURNAMENT_STATUS.FINISHED,
  TOURNAMENT_STATUS.CLOSED,
  TOURNAMENT_STATUS.REGISTRATION_CLOSED,
]);
const STALE_DAYS = 7;

function isActiveTournament(t: Tournament): boolean {
  if (CLOSED_STATUSES.has(t.status)) return false;
  // Backend leaves CHECKIN/STARTED/IN_PROGRESS forever even after the event is
  // over — filter by started_at age so the list isn't polluted by months-old
  // ghosts. Only OPENED/REGISTRATION_OPENED/PENDING (registration phase before
  // start) are allowed to live in the future regardless of age.
  const futureOk =
    t.status === TOURNAMENT_STATUS.OPENED ||
    t.status === TOURNAMENT_STATUS.REGISTRATION_OPENED ||
    t.status === TOURNAMENT_STATUS.PENDING;
  if (futureOk) return true;
  const startedMs = new Date(t.started_at).getTime();
  if (Number.isNaN(startedMs)) return true;
  return Date.now() - startedMs <= STALE_DAYS * 24 * 60 * 60 * 1000;
}

type ViewKey = 'tournaments' | 'challenges' | 'sitAndGo' | 'community' | 'live';

const REGULAR_TYPES = '1,2,3,4,5,6,7,9';
const SIT_AND_GO_TYPE = 12;
const CHALLENGE_TYPE = 8;

/**
 * QDuels lists poll slower than the Laravel ones on purpose: the qduels
 * tournament sweep runs every 15s, so a client polling faster than that just
 * re-reads the same document. (The Laravel 8s poll exists to catch a
 * CHECKIN→STARTED bump, a different backend with different timing.)
 */
const QDUELS_POLL_MS = 15000;

/**
 * QDuels statuses that earn a row.
 *
 * `complete` IS listed: the discovery scope (`?scope=open`) is registration-only,
 * so once a tournament of mine ends it drops out of every list — and with it the
 * final standings, reachable by direct URL only. `cancelled` stays out: it has
 * nothing to show.
 *
 * Finished ones live in their own section below the live list and don't feed
 * the tab counters — those count what you can still act on.
 */
const QDUELS_LISTED = new Set<string>(['registration', 'in_progress', 'complete']);

function isFinished(row: FusedRow): boolean {
  // Only the QDuels leg can be finished here: the Laravel rows are already
  // sieved by isActiveTournament(), which drops FINISHED/CLOSED outright.
  return row.source === 'qduels' && row.data.status === 'complete';
}


/**
 * A row in the fused main tab. Laravel and QDuels tournaments share no id
 * space, no status vocabulary and no seat semantics, so they travel as a
 * discriminated union and each renders through its own card.
 */
type FusedRow =
  | { source: 'laravel'; key: string; data: Tournament }
  | { source: 'qduels'; key: string; data: QduelsTournamentSummary };

/**
 * Rows for a kind's tab: the public open list PLUS my own active public ones.
 *
 * The discovery list (`?scope=open`) is registration-only, so a tournament I
 * joined drops out of it the moment it starts — a public sit&go auto-starts the
 * instant it fills. Without merging my own back in, it would be reachable only
 * by direct URL and the player would lose the way back to their live match.
 *
 * Dedupe by id: one I joined that is still open appears in both lists.
 */
function openPlusMineByKind(
  open: QduelsTournamentSummary[],
  mine: QduelsTournamentSummary[],
  kind: QduelsTournamentSummary['kind'],
): QduelsTournamentSummary[] {
  const seen = new Set<string>();
  const out: QduelsTournamentSummary[] = [];
  for (const t of [...open, ...mine.filter((m) => m.visibility === 'public')]) {
    if (t.kind !== kind || seen.has(t.tournamentId)) continue;
    seen.add(t.tournamentId);
    out.push(t);
  }
  return out;
}


/**
 * @param forcedView When set, the component is embedded (in a Hub panel): it
 *   pins that single view, hides its own tab bar, and renders only that view's
 *   list. Omitted → the standalone `/tournaments` route with its full tab bar.
 */
export function Tournaments({ forcedView }: { forcedView?: ViewKey } = {}) {
  const { t } = useTranslation('tournaments');
  const haptics = useHaptics();
  const activeGames = useAppSelector((s) => s.core.activeGames);
  const { activeGame } = useGameSelection();
  const gamesParam = activeGames.length ? activeGames.join(',') : undefined;

  const isCR = activeGame?.id === CR_GAME_ID;

  // 🔴 QUANDO LIMULO E' ACCESO, LA SCHEDA TORNEI **E'** LIMULO — non un ingresso accanto
  // ai tornei Laravel/QDuels, ma al loro posto (decisione di Luca, 2026-09-05:
  // «nella sezione tornei voglio vedere direttamente i tornei Limulo; in TMA non
  // esisteranno i tornei laravel e qduels»).
  //
  // Le DUE serrature restano identiche a quelle che proteggono le rotte in `router.tsx` —
  // flag di build in AND col ruolo `beta` — e devono restare tali: qui non proteggono piu'
  // un link ma la SOSTITUZIONE di una schermata viva, quindi allentarle non mostrerebbe
  // una sezione in piu', ne toglierebbe una che funziona.
  //
  // ⚠️ In PRODUZIONE non cambia nulla: `VITE_FEATURE_LIMULO_ENABLED` e' opt-in e
  // `.env.production` non lo dichiara, quindi questo ramo non esiste nel bundle.
  //
  // Forma inline (non <RoleGate>) perche' serve un BOOLEANO, non un wrapper: stessa scelta
  // gia' fatta in `components/hub/HubSections.tsx`.
  const isBeta = useHasLogtoRole('beta');
  const limuloIsTheSection = env.featureLimuloEnabled && isBeta;

  // The picked view drives the standalone route only; when `forcedView` is set
  // it wins and the tab bar is hidden.
  const [pickedView, setView] = useState<ViewKey>('tournaments');
  const view = forcedView ?? pickedView;
  // Reset the picked view when the active game changes (the secondary tab swaps
  // between Sit&Go/Challenges). No-op when a view is forced.
  useEffect(() => {
    if (forcedView) return;
    setView('tournaments');
  }, [activeGame?.id, forcedView]);

  // refetchOnMountOrArgChange + refetchOnFocus + short poll keep the seat
  // count from going stale: backend bumps participant_count as users join and
  // when a tournament transitions OPEN → CHECKIN → STARTED. Without this RTK
  // Query would pin to the original cached number until the user manually
  // reloads. 8s is aggressive enough to surface a CHECKIN→STARTED bump
  // within a single Mini App session without hammering the Laravel API.
  const POLL_MS = 8000;
  // ⚠️ `limuloReplacesThisView` si calcola QUI, prima delle query, e non piu' solo giu' al
  // ritorno anticipato. Il perche' e' misurato: il return sta **dopo** gli hook — deve, o
  // li salterebbe a condizione variabile — quindi senza uno skip **le query Laravel
  // partivano lo stesso e il risultato veniva buttato**. Con Limulo acceso e il monolite
  // spento erano 6 `tournaments` + 3 `my-tournaments` + un `exchange-token` per giro, su
  // una vista che quei dati non li mostra nemmeno.
  //
  // 📌 *Un ritorno anticipato nasconde il rendering, non il lavoro.* Le liste che non
  // vengono mostrate vanno spente dove NASCONO, non dove si renderizzano.
  const limuloReplacesThisView =
    limuloIsTheSection && (forcedView === undefined || forcedView === 'tournaments');

  // ⚠️ Le condizioni di skip hanno UNA copia sola, e non e' pulizia: la stessa condizione
  // serve due volte — per non fare la chiamata, e per dire a `listVerdict` che quella gamba
  // non e' raggiungibile. Due copie divergono, e la seconda a divergere farebbe affermare
  // uno stato che la prima non produce piu'.
  const skipTournaments = limuloReplacesThisView;
  const skipChallenges = isCR || limuloReplacesThisView;
  const skipSitAndGo = !isCR || limuloReplacesThisView;

  // 🔴 IL POLLING SI RICORDA DI UN RIFIUTO. Misurato da or45 il 2026-09-06 su Sit&Go con
  // Laravel di staging a 503: dopo che l'errore era GIA' a schermo, NOVE richieste in 60
  // secondi, una ogni 6-9 s, tutte 503 — batteria e traffico di una WebView di telefono
  // spesi contro un servizio che aveva gia' detto di non esserci.
  // ⛔ Solo un FALLIMENTO rallenta: un 200 con lista vuota e' una risposta buona e azzera.
  // ⚠️ `refetchOnFocus` e `refetchOnMountOrArgChange` restano accesi, quindi tornare sulla
  // scheda ritenta SUBITO: il backoff governa il ritmo di chi aspetta senza guardare.
  // Gradini e regole in `lib/pollingBackoff.ts`, esercitati dal gate.
  const [pollTracking, setPollTracking] = useState(NO_POLL_TRACKING);
  const pollMs = nextPollInterval(POLL_MS, pollTracking.streak);

  const queryOpts = {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    pollingInterval: pollMs,
    skip: skipTournaments,
  } as const;
  const tournamentsQuery = useTournamentsQuery(
    { limit: 50, games: gamesParam, tournament_type: REGULAR_TYPES },
    queryOpts,
  );
  const challengesQuery = useTournamentsQuery(
    { limit: 50, games: gamesParam, tournament_type: CHALLENGE_TYPE },
    { ...queryOpts, skip: skipChallenges },
  );
  // QDuels is Clash-Royale-only, so its lists exist only on the CR game — and
  // only behind the flag, since they need qduels-api's `?scope=open` discovery
  // endpoint to be deployed. With the flag OFF nothing here regresses: the tabs
  // fall back to exactly the Laravel sources they use today.
  const qduelsOn = env.featureQduelsTournamentsEnabled && isCR;

  // Sit&Go è FUSO come il tab Tornei: i sit&go Laravel (type 12) restano accanto
  // a quelli QDuels invece di essere sostituiti. La prima versione li saltava
  // quando QDuels era acceso ("QDuels sostituisce Laravel"), ma il risultato dal
  // vivo era che i sit&go Laravel esistenti sparivano dal tab — non c'è motivo
  // per cui la fusione valga nel tab principale e non qui.
  const sitAndGoQuery = useTournamentsQuery(
    { limit: 100, games: gamesParam, tournament_type: SIT_AND_GO_TYPE },
    { ...queryOpts, skip: skipSitAndGo },
  );

  /**
   * Discovery: every registration-open `visibility: 'public'` tournament. One
   * request feeds TWO tabs — qduels has no `kind` filter on this scope (and
   * rejects `role`/`status` outright), so standard vs sit&go is split
   * client-side below.
   *
   * NB: only the first page is read. Paging exists (`nextCursor`) but a filtered
   * page yields a variable number of rows per tab, so a "load more" needs to
   * page until each tab fills — deferred until the volumes justify it.
   */
  const qduelsOpenQuery = useListOpenTournamentsQuery(
    { limit: 50 },
    {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      pollingInterval: QDUELS_POLL_MS,
      skip: !qduelsOn || limuloReplacesThisView,
    },
  );

  /**
   * The Community list: channel-bound tournaments that are MINE — joined or
   * organised. There is no way to browse a community's tournaments (by design,
   * qduels' default list is caller-scoped: "never a global list"), which is
   * exactly the product rule: you see a community tournament only if you joined
   * it through that channel's bot.
   *
   * `role` is deliberately OMITTED, not set to 'participant': omitting it makes
   * qduels return `organizer OR participant`. With `role: 'participant'` a
   * tournament you CREATED but never joined is invisible everywhere — it isn't
   * in the open list either (a standard is born `community`), so the organiser
   * loses their own tournament entirely.
   *
   * KNOWN LIMITS (server-side, not fixable here): capped at 50 with NO cursor
   * (qduels deliberately omits nextCursor on the default scope), and `status` is
   * a single-value filter, so "registration OR in_progress" is filtered
   * client-side after the fact. A user whose 50 most recent tournaments (any
   * status) crowd out an older live one loses it from the list.
   */
  const qduelsMineQuery = useListMyTournamentsQuery(
    { limit: 50 },
    {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      pollingInterval: QDUELS_POLL_MS,
      skip: !qduelsOn || limuloReplacesThisView,
    },
  );

  /**
   * The LIVE list: public tournaments being played right now
   * (`scope=open&status=in_progress`). This is the only list from which a
   * non-participant can find a match to watch — the joinable discovery
   * (`qduelsOpenQuery`, registration-only) never shows in-progress ones, and the
   * caller-scoped list only ever shows a stranger their OWN. Not kind-split: the
   * Live tab is a watch surface, so a live sit&go is as watchable as a standard.
   *
   * The tournament stays in_progress across all its matches (longer than any one
   * ~2-3 min bo1), so 15s discovery polling is plenty; the short per-match
   * spectator window is caught by the slots poll inside the detail screen.
   */
  const qduelsLiveQuery = useListOpenTournamentsQuery(
    { status: 'in_progress', limit: 50 },
    {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      pollingInterval: QDUELS_POLL_MS,
      skip: !qduelsOn || limuloReplacesThisView,
    },
  );

  const qduelsOpen = useMemo(() => qduelsOpenQuery.data?.tournaments ?? [], [qduelsOpenQuery.data]);
  const qduelsLive = useMemo(
    () => qduelsLiveQuery.data?.tournaments ?? [],
    [qduelsLiveQuery.data],
  );
  const qduelsMine = useMemo(
    () => (qduelsMineQuery.data?.tournaments ?? []).filter((t) => QDUELS_LISTED.has(t.status)),
    [qduelsMineQuery.data],
  );

  const qduelsStandard = useMemo(
    () => openPlusMineByKind(qduelsOpen, qduelsMine, 'standard'),
    [qduelsOpen, qduelsMine],
  );
  const qduelsSitngo = useMemo(
    () => openPlusMineByKind(qduelsOpen, qduelsMine, 'sitngo'),
    [qduelsOpen, qduelsMine],
  );

  /**
   * Community = tournaments bound to a channel that I joined through that
   * channel's bot. Keyed on `visibility`, NOT on `guildId`: qduels added
   * `visibility` precisely so intent isn't inferred from the guild — keying on
   * guildId would both miss community tournaments created in a DM and
   * double-count a public one announced in a channel (it would show here AND
   * in its own tab).
   */
  const qduelsCommunity = useMemo(
    () => qduelsMine.filter((t) => t.visibility === 'community'),
    [qduelsMine],
  );

  /**
   * The Laravel query backing the current view (QDuels views have their own).
   * `community` has no Laravel leg at all now — the old `christmas25`-tagged
   * list was a finished campaign and was removed with it.
   */
  const activeLaravelSkipped =
    view === 'sitAndGo' ? skipSitAndGo : view === 'challenges' ? skipChallenges : skipTournaments;
  const activeLaravelQuery = useMemo(() => {
    switch (view) {
      case 'sitAndGo':
        return sitAndGoQuery;
      case 'challenges':
        return challengesQuery;
      // Community and Live are QDuels-only surfaces — no Laravel leg. Live in
      // particular must NOT fall through to the standard query, or the watch
      // list would be polluted with unrelated Laravel tournaments.
      case 'community':
      case 'live':
        return undefined;
      case 'tournaments':
      default:
        return tournamentsQuery;
    }
  }, [view, tournamentsQuery, challengesQuery, sitAndGoQuery]);

  const laravelRows: Tournament[] = useMemo(
    () => (activeLaravelQuery?.data?.data ?? []).filter(isActiveTournament),
    [activeLaravelQuery],
  );

  /** The QDuels leg of the current view. */
  const qduelsRows: QduelsTournamentSummary[] = useMemo(() => {
    if (!qduelsOn) return [];
    switch (view) {
      case 'tournaments':
        return qduelsStandard;
      case 'sitAndGo':
        return qduelsSitngo;
      case 'community':
        return qduelsCommunity;
      case 'live':
        return qduelsLive;
      default:
        return [];
    }
  }, [qduelsOn, view, qduelsStandard, qduelsSitngo, qduelsCommunity, qduelsLive]);

  /**
   * Fused rows for the active tab.
   *
   * Order: QDuels first, then Laravel in backend order. The QDuels leg is the
   * actionable half (open to join, or your own live one), so it goes on top;
   * the Laravel leg keeps whatever order the monolith returned (it has no
   * comparable sort key: `started_at` is in the past for in-progress events and
   * a sit&go has no start date at all, so interleaving by date would shuffle the
   * two backends into an order neither of them means).
   */
  const rows: FusedRow[] = useMemo(
    () => [
      ...qduelsRows.map(
        (data): FusedRow => ({ source: 'qduels', key: `q:${data.tournamentId}`, data }),
      ),
      ...laravelRows.map((data): FusedRow => ({ source: 'laravel', key: `l:${data.id}`, data })),
    ],
    [qduelsRows, laravelRows],
  );

  /**
   * Two lists, not one sorted list: a finished tournament is a different kind
   * of thing from an open one (you read it, you don't join it), so it gets its
   * own section under its own heading rather than trailing the live rows where
   * it reads as "just further down the same queue".
   */
  const [liveRows, finishedRows] = useMemo(() => {
    const live: FusedRow[] = [];
    const done: FusedRow[] = [];
    for (const row of rows) (isFinished(row) ? done : live).push(row);
    return [live, done];
  }, [rows]);

  const qduelsQuery =
    view === 'community'
      ? qduelsMineQuery
      : view === 'live'
        ? qduelsLiveQuery
        : view === 'tournaments' || view === 'sitAndGo'
          ? qduelsOpenQuery
          : undefined;
  // ⚠️ `qduelsActive` e `isLoading` sono spariti da qui il 2026-09-06, e non per pulizia:
  // erano DUE booleani che decidevano insieme cosa mostrare, e la combinazione «nessuno
  // dei due vero» era il buco da cui usciva il vuoto affermato. Ora la decisione e' UNA
  // sola, totale, e vive in una funzione pura esercitata dal gate.
  //
  // 🔴 Un vuoto si DICHIARA solo se il server ha risposto. Misurato il 2026-09-05 su
  // `stg-tma` col monolite spento (503 ovunque): questa scheda mostrava «No tournaments in
  // this category» mentre il backend rifiutava tutto — un vuoto **affermato**, che e' peggio
  // sia dello spinner (che tace) sia dell'errore (che dice di non sapere).
  //
  // La causa era la premessa, non il ramo: il vuoto veniva dedotto da tre stati del CLIENT
  // (`!isLoading && !isError && rows.length === 0`), ma `isLoading` e' vero solo al PRIMO
  // caricamento — dopo, un giro di `pollingInterval` e' `isFetching` — e `isError` e' lo
  // stato CORRENTE. Fra un tentativo fallito e il successivo esiste una finestra in cui
  // nessuno dei tre e' vero, ed e' li' che l'app affermava.
  //
  // `data !== undefined` guarda il SERVER: RTK Query lo popola solo con una risposta
  // riuscita e non lo cancella su un errore successivo. Regole e casi in `lib/listState.ts`,
  // esercitate da `tools/verify-tournaments.ts`.
  //
  // 🔴 AGGIORNATO il 2026-09-06, e la causa e' un caso che `canClaimEmpty` non copriva.
  // Su `stg-tma` con tutti i tab accesi, `VITE_FEATURE_QDUELS_TOURNAMENTS_ENABLED=false`
  // mette le query qduels in `skip`: non caricano, non falliscono, non rispondono. La
  // scheda COMMUNITY — che non ha gamba Laravel — restava quindi con ZERO gambe attive, e
  // `canClaimEmpty(0, [])` risponde `true` **per scelta esplicita**: mostrava «No
  // tournaments in this category» su una domanda mai posta.
  //
  // `listVerdict` distingue cio' che `active` confondeva: una fonte NON PERTINENTE non si
  // passa affatto (la gamba Laravel di `community` non esiste per prodotto, e tacere e'
  // giusto), una fonte pertinente ma SPENTA si passa con `reachable: false` e produce
  // `unavailable`. Dettaglio e precedenze in `lib/listState.ts`.
  // ⚠️ Una richiesta che non torna MAI non produce ne' `isError` ne' `data`: `isLoading`
  // resta vero e lo spinner gira per sempre. `useStalled` non annulla la chiamata — cambia
  // solo cio' che lo schermo DICE nel frattempo, da «sto caricando» a «non lo so».
  const laravelAnswered = activeLaravelQuery?.data !== undefined;
  const qduelsAnswered = qduelsQuery?.data !== undefined;
  // ⚠️ `isError` lampeggia come `isLoading`, in senso opposto: senza memoria la scheda
  // alternerebbe l'errore vero e «ci sta mettendo troppo» a ogni giro di polling.
  const laravelFailed = useEverFailed(activeLaravelQuery?.isError ?? false, laravelAnswered);
  const qduelsFailed = useEverFailed(qduelsQuery?.isError ?? false, qduelsAnswered);
  // 🔴 L'ESITO SI LEGGE DAL TENTATIVO, NON DALLO STATO CUMULATIVO — e la prima stesura di
  // questo blocco faceva il contrario, quindi il backoff si armava SOLO su una query che non
  // aveva mai risposto: se il backend cadeva DOPO il primo 200 — il caso piu' comune, e
  // quello vero di quella sera — il polling restava a 8 s per sempre.
  // Due cause, entrambe verificate nel sorgente di RTK in `node_modules`: `rejected` non
  // tocca `fulfilledTimeStamp` (la chiave si congelava) e non cancella `data` (il predicato
  // «ha risposto» restava vero). Regole e banco in `lib/pollingBackoff.ts`.
  // ⚠️ Si conta UN esito per TENTATIVO, non per render: `trackPollOutcome` e' idempotente
  // sullo stesso `startedTimeStamp`, o un solo rifiuto porterebbe la scheda al tetto.
  const laravelStarted = activeLaravelQuery?.startedTimeStamp;
  const laravelFulfilled = activeLaravelQuery?.fulfilledTimeStamp;
  const laravelIsError = activeLaravelQuery?.isError ?? false;
  useEffect(() => {
    if (activeLaravelQuery === undefined) return;
    setPollTracking((prev) =>
      trackPollOutcome(prev, {
        startedTimeStamp: laravelStarted,
        fulfilledTimeStamp: laravelFulfilled,
        isError: laravelIsError,
      }),
    );
  }, [activeLaravelQuery, laravelStarted, laravelFulfilled, laravelIsError]);

  const laravelPending = activeLaravelQuery !== undefined && !laravelAnswered && !laravelFailed;
  const qduelsPending = qduelsOn && qduelsQuery !== undefined && !qduelsAnswered && !qduelsFailed;
  const laravelStalled = useStalled(laravelPending);
  const qduelsStalled = useStalled(qduelsPending);
  // 🔴 La seconda soglia: oltre questa la fonte non e' «lenta», e' MUTA. Senza di essa una
  // chiamata che ne' risponde ne' fallisce lasciava «Ci sta mettendo piu' del previsto» a
  // schermo per minuti — visto da una giocatrice vera il 2026-09-06.
  const laravelNoAnswer = useStalled(laravelPending, NO_ANSWER_MS);
  const qduelsNoAnswer = useStalled(qduelsPending, NO_ANSWER_MS);
  const verdict = listVerdict(rows.length, [
    ...(activeLaravelQuery
      ? [
          {
            // ⛔ Non cablato a `true`: una query in `skip` non e' una fonte che tace, e' una
            // fonte che non stiamo interrogando — e su una vista dove e' l'unica, e' proprio
            // il caso che questa modifica esiste per dire.
            reachable: !activeLaravelSkipped,
            // ⛔ `loading` e' SOLO `isLoading`. Metterci `|| laravelStalled` rende `loading`
            // vero per costruzione appena scatta la scadenza, e da quel momento il ramo
            // «muta e non sta caricando ⇒ errore» non puo' piu' scattare.
            // ⚠️ **PRECISATO il 2026-09-06 dopo una misura di or45, perche' la prima stesura
            // di questa riga diceva troppo**: dei DUE modi di arrivare all'errore quell'OR ne
            // chiudeva UNO SOLO. Se la richiesta TORNA con un errore, `failed` scatta lo
            // stesso e la frase cede (misurato su Sit&Go: errore a ~21 s, regolarmente).
            // Il blocco eterno esiste solo quando la richiesta **non torna MAI** — ed e'
            // esattamente il caso di Martha.
            // 📌 *Chiudere il caso dicendo «era l'OR» lascerebbe la pendenza li', invisibile
            // fino al prossimo utente.* La causa radice e' un'altra e sta in `logto.ts`.
            loading: activeLaravelQuery.isLoading,
            slow: laravelStalled,
            failed: laravelFailed || laravelNoAnswer,
            answered: laravelAnswered,
          },
        ]
      : []),
    // 🔴 QUANDO la gamba qduels ha diritto di parola: in una vista FUSA con Laravel spenta
    // non e' irraggiungibile, e' NON PERTINENTE — Laravel ha risposto per intero. Passarla
    // comunque rendeva «Non disponibile su questo backend» le schede Eventi e Sit&Go **in
    // produzione**, dove il flag qduels e' opt-in e non dichiarato: la stessa affermazione
    // falsa che questa modifica combatte, col segno invertito e sulla superficie viva.
    // Trovata in revisione il 2026-09-06, prima del rilascio. Regola e casi in
    // `lib/tournamentLegs.ts`, esercitati coi valori di produzione dal gate.
    ...(qduelsLegIsPertinent(view, qduelsQuery !== undefined, qduelsOn) && qduelsQuery
      ? [
          {
            reachable: qduelsOn,
            loading: qduelsQuery.isLoading,
            slow: qduelsStalled,
            failed: qduelsFailed || qduelsNoAnswer,
            answered: qduelsAnswered,
          },
        ]
      : []),
  ]);
  const isError = verdict === 'error';

  // A 401 after the exchange-token retry means the Laravel session is broken
  // (e.g. unverified-email reconciler limbo) — say so instead of a generic
  // "failed to load".
  const authFailed =
    isError &&
    (activeLaravelQuery?.error as { status?: number } | undefined)?.status === 401;

  const tabs: ReadonlyArray<TabDef> = useMemo(() => {
    const list: TabDef[] = [
      {
        key: 'tournaments',
        Icon: Trophy,
        label: t('tabs.tournaments', { defaultValue: 'Tournaments' }),
      },
      isCR
        ? {
            key: 'sitAndGo',
            Icon: Zap,
            label: t('tabs.sitAndGo', { defaultValue: 'Sit&Go' }),
          }
        : {
            key: 'challenges',
            Icon: Mountain,
            label: t('tabs.challenges', { defaultValue: 'Challenges' }),
          },
    ];
    // Live + Community are QDuels-only surfaces (they need the discovery
    // endpoint), so they appear only where QDuels does. Live is the watch list —
    // the only place a non-participant finds an ongoing match to spectate.
    if (qduelsOn) {
      list.push({
        key: 'live',
        Icon: Radio,
        label: t('tournaments.liveList.tab', { ns: 'duels', defaultValue: 'Live' }),
      });
      list.push({
        key: 'community',
        Icon: Heart,
        label: t('tabs.community', { defaultValue: 'Community' }),
      });
    }
    return list;
  }, [t, isCR, qduelsOn]);

  // Reset to the default tab if the visible set shrinks under the current view
  // (e.g. the game switches away from CR while sitting on Community).
  useEffect(() => {
    if (forcedView) return;
    if (!tabs.some((tab) => tab.key === view)) setView('tournaments');
  }, [tabs, view, forcedView]);

  const switchView = (next: ViewKey) => {
    if (next === view) return;
    haptics.selection();
    setView(next);
  };

  // ⚠️ Il ritorno anticipato sta QUI e non piu' in alto: sopra ci sono gli hook, e un
  // `return` prima di loro li salterebbe a condizione variabile. Sotto, invece, ogni hook
  // e' gia' stato chiamato.
  //
  // 🔴 RIVISTA il 2026-09-05 sera, e la riga precedente diceva l'opposto: «la sostituzione
  // vale per la SCHEDA Tornei, non per il Hub». Era una scelta deliberata, ed e' stata
  // cambiata su misura, non su opinione.
  //
  // Il fatto che l'ha cambiata: il Hub e' **la porta da cui l'operatore guarda**, e col
  // monolite di staging spento mostrava «Errore nel caricamento dei tornei» mentre la
  // sezione Limulo, a due tap di distanza, serviva gli stessi tornei senza un errore. Due
  // liste di tornei, una viva e una morta, dietro la stessa parola.
  //
  // ⚠️ SOLO il pannello EVENTI, e non e' timidezza: `sitAndGo` e `community` sono concetti
  // di QDuels e del monolite che su Limulo **non hanno controparte**. Sostituirli
  // mostrerebbe la lista sbagliata sotto un'etichetta giusta — che e' peggio di un vuoto,
  // per la stessa ragione per cui e' peggio un vuoto affermato di uno spinner.
  //
  // `embedded`: il pannello del Hub ha gia' la propria intestazione e le proprie schede.
  // (`limuloReplacesThisView` e' calcolato sopra, prima delle query, perche' le spegne.)
  if (limuloReplacesThisView) {
    return (
      <Suspense fallback={<Loader />}>
        <LimuloTournaments embedded={forcedView !== undefined} />
      </Suspense>
    );
  }

  return (
    // Fixed viewport: the page itself never scrolls, only the list does, so the
    // tabs stay put instead of sliding away under the thumb. `h-full` fills
    // AppShell's <main>; `overflow-hidden` keeps <main>'s own scroller idle.
    // (The document can't scroll either — html/body are locked in globals.css.)
    // No page title and no <GameHeader />: the tabs already say where you are,
    // and the game is picked from the Hub — one selector, one place.
    // Il padding verticale vale solo da rotta AUTONOMA. Incorporato nella Hub
    // (`forcedView`) lo spazio lo mette gia' la Hub, e sommarli rubava altezza
    // alla lista proprio dove ce n'e' meno — sotto la griglia delle sezioni.
    <div className={cn('flex h-full flex-col overflow-hidden px-4', forcedView ? '' : 'py-3')}>
      {/* Tab bar only in the standalone route; hidden when embedded in the Hub
          (the Hub's own section buttons pick the view). */}
      {!forcedView && (
        <div
          role="tablist"
          className={cn(
            'shrink-0 grid gap-2 pb-2',
            // 4 tabs wrap to two rows of two; 2 fill one row; 3 stay a triple.
            tabs.length === 2 || tabs.length === 4 ? 'grid-cols-2' : 'grid-cols-3',
          )}
        >
          {tabs.map((tab) => (
            <CategoryTab
              key={tab.key}
              tab={tab}
              active={view === tab.key}
              onClick={() => switchView(tab.key)}
            />
          ))}
        </div>
      )}

      {/* Card-finestra: la superficie resta FERMA e scorre solo il contenuto,
          così il suo bordo superiore non esce dallo schermo e la texture a
          scaglie non passa dietro le righe. Stessa struttura di Battaglie. */}
      <SectionCard flush className="flex min-h-0 flex-col overflow-hidden">
        <div className="min-h-0 overflow-y-auto overscroll-contain p-3 [touch-action:pan-y]">
        {(verdict === 'loading' || verdict === 'slow') && (
          <div className="flex h-40 items-center justify-center">
            <PanelWaiting slow={verdict === 'slow'} />
          </div>
        )}

        {verdict === 'error' && (
          <div className="rounded-xl bg-bg-secondary p-6 text-center text-sm text-danger">
            {authFailed
              ? t('load_error_auth', {
                  defaultValue:
                    'Your session is not active. Close and reopen the app — and if you just signed up, verify your email first.',
                })
              : t('load_error', { defaultValue: 'Failed to load tournaments.' })}
          </div>
        )}

        {/* La fonte di questa scheda e' spenta su questa build: non e' un vuoto e non e'
            un errore, ed e' l'unico dei tre che finora non sapevamo dire. */}
        {verdict === 'unavailable' && <PanelNotice tone="unavailable" className="py-10" />}

        {verdict === 'empty' && (
          <EmptyState>
            {view === 'community'
              ? t('community_empty', {
                  defaultValue:
                    "You'll see a community tournament here once you join one from that channel's bot.",
                })
              : view === 'live'
                ? t('tournaments.liveList.empty', {
                    ns: 'duels',
                    defaultValue: 'No tournaments are being played right now. Check back when one is live.',
                  })
                : t('empty', { defaultValue: 'No tournaments in this category.' })}
          </EmptyState>
        )}

        <TournamentRows rows={liveRows} view={view} />

        {finishedRows.length > 0 && (
          <>
            <div className="flex items-center gap-3 pb-2 pt-5">
              <span className="text-[11px] font-bold uppercase tracking-wide text-fg-muted">
                {t('finished_section', { defaultValue: 'Finished' })}
              </span>
              <span aria-hidden className="h-px flex-1 bg-border" />
            </div>
            {/* Dimmed as a group: the section heading says what these are, the
                opacity says it again without a badge on every row. */}
            <TournamentRows rows={finishedRows} view={view} className="opacity-60" />
          </>
        )}
        </div>
      </SectionCard>
    </div>
  );
}

/** One list of fused rows. Rendered twice: the live section and the finished one. */
function TournamentRows({
  rows,
  view,
  className,
}: {
  rows: FusedRow[];
  view: ViewKey;
  className?: string;
}) {
  if (rows.length === 0) return null;
  return (
    <ul className={cn('flex flex-col gap-2', className)}>
      {rows.map((row) => (
        <li key={row.key}>
          {row.source === 'qduels' ? (
            <QduelsTournamentCard
              tournament={row.data}
              // The badge earns its place wherever the two backends coexist
              // (Tornei and Sit&Go). In Community every row is a duel, so it
              // would be noise.
              showSourceBadge={view === 'tournaments' || view === 'sitAndGo'}
            />
          ) : (
            <TournamentCard tournament={row.data} variant="full" />
          )}
        </li>
      ))}
    </ul>
  );
}

interface TabDef {
  key: ViewKey;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
}

function CategoryTab({
  tab,
  active,
  onClick,
}: {
  tab: TabDef;
  active: boolean;
  onClick: () => void;
}) {
  const { Icon, label } = tab;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'relative flex items-center justify-center gap-1.5 rounded-xl px-2 py-2',
        'transition-colors active:opacity-80',
        active ? 'bg-brand text-brand-fg' : 'bg-bg-secondary text-fg-muted',
      )}
    >
      <Icon aria-hidden className="h-4 w-4 shrink-0" />
      <span className="truncate text-[11px] font-bold uppercase tracking-wide">{label}</span>
    </button>
  );
}
